import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API, SHOW_PRICING } from '../App';
import { DashboardLayout } from '../components/DashboardLayout';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  Briefcase, Search, Plus, ExternalLink, Mail, Eye, MessageSquare,
  Lock, X, Handshake, Users, Sparkles, Crown
} from 'lucide-react';

const BusinessDirectory = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('directory');
  const [categories, setCategories] = useState([]);
  const [collaborationTypes, setCollaborationTypes] = useState([]);
  const [listings, setListings] = useState([]);
  const [myListing, setMyListing] = useState(null);
  const [collaborationPosts, setCollaborationPosts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCollabModal, setShowCollabModal] = useState(false);
  const [listingForm, setListingForm] = useState({
    business_name: '', category: '', description: '', services: [],
    pricing_info: '', contact_email: '', website_url: '', social_links: {}
  });
  const [collabForm, setCollabForm] = useState({
    title: '', description: '', collaboration_type: '', requirements: ''
  });
  const [newService, setNewService] = useState('');

  const tier = user?.subscription_tier || 'free';
  const subscriptionStatus = user?.subscription_status || 'inactive';
  const isBetaUser = user?.is_beta_user || user?.beta_access || subscriptionStatus === 'beta';
  const hasAccess = !SHOW_PRICING || tier === 'pro' || tier === 'experience' || isBetaUser;

  useEffect(() => {
    fetchData();
  }, [filterCategory]);

  const fetchData = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const [categoriesRes, listingsRes, myListingRes, collabRes] = await Promise.all([
        axios.get(`${API}/business-directory/categories`),
        axios.get(`${API}/business-directory/listings`, { 
          params: { category: filterCategory || undefined },
          headers, 
           
        }),
        hasAccess ? axios.get(`${API}/business-directory/my-listing`, { headers }) : Promise.resolve({ data: { listing: null } }),
        axios.get(`${API}/collaboration-hub/posts`, { headers })
      ]);
      
      setCategories(categoriesRes.data.categories);
      setCollaborationTypes(categoriesRes.data.collaboration_types);
      setListings(listingsRes.data.listings);
      setMyListing(myListingRes.data.listing);
      setCollaborationPosts(collabRes.data.posts);
      
      if (myListingRes.data.listing) {
        setListingForm(myListingRes.data.listing);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const createListing = async () => {
    if (!listingForm.business_name || !listingForm.category || !listingForm.description) {
      toast.error('Please fill in required fields');
      return;
    }
    
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/business-directory/listing`, listingForm, { headers });
      toast.success('Listing created!');
      setShowCreateModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create listing');
    }
  };

  const createCollabPost = async () => {
    if (!collabForm.title || !collabForm.collaboration_type || !collabForm.description) {
      toast.error('Please fill in required fields');
      return;
    }
    
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/collaboration-hub/post`, collabForm, { headers });
      toast.success('Collaboration post created!');
      setShowCollabModal(false);
      setCollabForm({ title: '', description: '', collaboration_type: '', requirements: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create post');
    }
  };

  const addService = () => {
    if (newService && !listingForm.services.includes(newService)) {
      setListingForm({ ...listingForm, services: [...listingForm.services, newService] });
      setNewService('');
    }
  };

  const removeService = (service) => {
    setListingForm({ ...listingForm, services: listingForm.services.filter(s => s !== service) });
  };

  if (!hasAccess) {
    return (
      <DashboardLayout title="Business Directory & Collaboration Hub">
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="card-gold p-16 text-center">
            <Lock className="text-[#D4AF37] mx-auto mb-6" size={60} />
            <h2 className="font-serif text-3xl text-white mb-4">Pro Feature</h2>
            <p className="text-[#A1A1AA] mb-8 max-w-md mx-auto">
              List your business, find collaborators, and connect with potential partners.
              Exclusive to Pro members.
            </p>
            <Button onClick={() => navigate('/dashboard')} className="btn-gold px-10 py-4" data-testid="upgrade-directory-btn">
              Upgrade to Pro
            </Button>
          </motion.div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Business Directory & Collaboration Hub">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="card-gold p-8 mb-8 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-radial from-[#D4AF37]/20 to-transparent rounded-full blur-3xl" />
          
          <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Crown className="text-[#D4AF37]" size={16} />
                <span className="font-accent text-[#D4AF37] text-[10px] tracking-[0.25em]">PRO EXCLUSIVE</span>
              </div>
              
              <h2 className="font-serif text-3xl text-white mb-2">Grow Your Business</h2>
              <p className="text-[#A1A1AA] font-light">
                Showcase your services and find collaboration opportunities.
              </p>
            </div>

            <div className="flex gap-3">
              <Button onClick={() => setShowCreateModal(true)} className="btn-outline-gold px-6 py-3" data-testid="create-listing-btn">
                <Briefcase size={16} className="mr-2" />
                {myListing ? 'Edit Listing' : 'Create Listing'}
              </Button>
              <Button onClick={() => setShowCollabModal(true)} className="btn-gold px-6 py-3" data-testid="create-collab-btn">
                <Handshake size={16} className="mr-2" />
                Post Collaboration
              </Button>
            </div>
          </div>
        </motion.div>

        {/* Tabs */}
        <div className="flex gap-2 mb-8 border-b border-[#1C1C1C] pb-4">
          {[
            { id: 'directory', label: 'Business Directory', icon: Briefcase },
            { id: 'collaborations', label: 'Collaboration Hub', icon: Handshake }
          ].map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-3 flex items-center gap-2 transition-all ${
                  activeTab === tab.id
                    ? 'text-[#E5C585] bg-[#E5C585]/5 border-b-2 border-[#D4AF37]'
                    : 'text-[#71717A] hover:text-white'
                }`}
              >
                <Icon size={18} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Directory View */}
        {activeTab === 'directory' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            {/* Filters */}
            <div className="card-premium p-4 mb-6 flex flex-wrap items-center gap-4">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#52525B]" size={18} />
                  <Input
                    placeholder="Search businesses..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 bg-transparent border-[#27272A]"
                  />
                </div>
              </div>
              
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="bg-[#0A0A0A] border border-[#27272A] text-[#A1A1AA] px-4 py-2 text-sm"
              >
                <option value="">All Categories</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>

            {/* Listings Grid */}
            <div className="grid md:grid-cols-2 gap-6">
              {listings.filter(l => 
                !searchQuery || 
                l.business_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                l.description?.toLowerCase().includes(searchQuery.toLowerCase())
              ).map((listing, index) => (
                <motion.div
                  key={listing.listing_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="card-premium p-6"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start gap-4">
                      {listing.owner_picture ? (
                        <img src={listing.owner_picture} alt={listing.owner_name} className="w-12 h-12 rounded-full" />
                      ) : (
                        <div className="w-12 h-12 bg-[#1C1C1C] rounded-full flex items-center justify-center">
                          <Briefcase className="text-[#E5C585]" size={20} />
                        </div>
                      )}
                      <div>
                        <h4 className="text-white font-medium">{listing.business_name}</h4>
                        <p className="text-[#D4AF37] text-xs font-accent tracking-wider">
                          {categories.find(c => c.id === listing.category)?.name}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-[#52525B] text-xs">
                      <Eye size={12} />
                      {listing.views}
                    </div>
                  </div>

                  <p className="text-[#A1A1AA] text-sm mb-4 line-clamp-2">{listing.description}</p>

                  {listing.services && listing.services.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-4">
                      {listing.services.slice(0, 3).map(service => (
                        <span key={service} className="text-[10px] px-2 py-1 bg-[#1C1C1C] text-[#A1A1AA] border border-[#27272A]">
                          {service}
                        </span>
                      ))}
                      {listing.services.length > 3 && (
                        <span className="text-[10px] text-[#52525B]">+{listing.services.length - 3} more</span>
                      )}
                    </div>
                  )}

                  <div className="flex items-center gap-2 pt-4 border-t border-[#1C1C1C]">
                    {listing.website_url && (
                      <a href={listing.website_url} target="_blank" rel="noopener noreferrer" className="p-2 bg-[#0A0A0A] border border-[#27272A] text-[#71717A] hover:text-[#E5C585] transition-colors">
                        <ExternalLink size={16} />
                      </a>
                    )}
                    {listing.contact_email && (
                      <a href={`mailto:${listing.contact_email}`} className="p-2 bg-[#0A0A0A] border border-[#27272A] text-[#71717A] hover:text-[#E5C585] transition-colors">
                        <Mail size={16} />
                      </a>
                    )}
                    <div className="flex-1" />
                    <span className="text-[#52525B] text-xs">{listing.inquiries} inquiries</span>
                  </div>
                </motion.div>
              ))}
            </div>

            {listings.length === 0 && (
              <div className="card-premium p-16 text-center">
                <Briefcase className="text-[#27272A] mx-auto mb-4" size={60} />
                <h3 className="font-serif text-xl text-white mb-2">No Listings Yet</h3>
                <p className="text-[#52525B]">Be the first to showcase your business!</p>
              </div>
            )}
          </motion.div>
        )}

        {/* Collaborations View */}
        {activeTab === 'collaborations' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="space-y-6">
              {collaborationPosts.map((post, index) => {
                const collabType = collaborationTypes.find(t => t.id === post.collaboration_type);
                return (
                  <motion.div
                    key={post.post_id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="card-premium p-6"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-start gap-4">
                        {post.author_picture ? (
                          <img src={post.author_picture} alt={post.author_name} className="w-10 h-10 rounded-full" />
                        ) : (
                          <div className="w-10 h-10 bg-[#1C1C1C] rounded-full flex items-center justify-center">
                            <span className="text-[#E5C585] font-serif text-sm">{post.author_name?.charAt(0)}</span>
                          </div>
                        )}
                        <div>
                          <h4 className="text-white font-medium">{post.title}</h4>
                          <p className="text-[#52525B] text-sm">by {post.author_name}</p>
                        </div>
                      </div>
                      <span className="text-[10px] px-2 py-1 bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/20">
                        {collabType?.name}
                      </span>
                    </div>

                    <p className="text-[#A1A1AA] text-sm mb-4">{post.description}</p>

                    {post.requirements && (
                      <div className="p-3 bg-[#0A0A0A] border border-[#27272A] mb-4">
                        <span className="text-[#52525B] text-xs font-accent tracking-wider">REQUIREMENTS</span>
                        <p className="text-[#A1A1AA] text-sm mt-1">{post.requirements}</p>
                      </div>
                    )}

                    <div className="flex items-center justify-between pt-4 border-t border-[#1C1C1C]">
                      <span className="text-[#52525B] text-xs">{post.responses} responses</span>
                      <Button className="btn-outline-gold px-4 py-2 text-sm">
                        <MessageSquare size={14} className="mr-1" />
                        Respond
                      </Button>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {collaborationPosts.length === 0 && (
              <div className="card-premium p-16 text-center">
                <Handshake className="text-[#27272A] mx-auto mb-4" size={60} />
                <h3 className="font-serif text-xl text-white mb-2">No Collaborations Yet</h3>
                <p className="text-[#52525B]">Post the first collaboration opportunity!</p>
              </div>
            )}
          </motion.div>
        )}

        {/* Create Listing Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="card-luxury p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-serif text-2xl text-white">Business Listing</h3>
                <button onClick={() => setShowCreateModal(false)} className="text-[#52525B] hover:text-white">
                  <X size={20} />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="text-[#A1A1AA] text-sm mb-2 block">Business Name *</label>
                  <Input
                    value={listingForm.business_name}
                    onChange={(e) => setListingForm({ ...listingForm, business_name: e.target.value })}
                    placeholder="Your business name"
                    className="input-underline"
                  />
                </div>

                <div>
                  <label className="text-[#A1A1AA] text-sm mb-2 block">Category *</label>
                  <select
                    value={listingForm.category}
                    onChange={(e) => setListingForm({ ...listingForm, category: e.target.value })}
                    className="w-full bg-transparent border-b border-[#27272A] focus:border-[#D4AF37] py-3 text-white outline-none"
                  >
                    <option value="">Select category</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-[#A1A1AA] text-sm mb-2 block">Description *</label>
                  <Textarea
                    value={listingForm.description}
                    onChange={(e) => setListingForm({ ...listingForm, description: e.target.value })}
                    placeholder="Describe your business..."
                    className="bg-transparent border border-[#27272A] focus:border-[#D4AF37] rounded-none p-3 text-white min-h-[100px]"
                  />
                </div>

                <div>
                  <label className="text-[#A1A1AA] text-sm mb-2 block">Services</label>
                  <div className="flex gap-2 mb-2">
                    <Input
                      value={newService}
                      onChange={(e) => setNewService(e.target.value)}
                      placeholder="Add a service"
                      className="input-underline flex-1"
                      onKeyPress={(e) => e.key === 'Enter' && addService()}
                    />
                    <Button onClick={addService} className="btn-outline-gold px-4">
                      <Plus size={16} />
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {listingForm.services?.map(service => (
                      <span key={service} className="px-3 py-1 bg-[#D4AF37]/10 text-[#E5C585] text-sm flex items-center gap-2">
                        {service}
                        <button onClick={() => removeService(service)}><X size={12} /></button>
                      </span>
                    ))}
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-[#A1A1AA] text-sm mb-2 block">Contact Email</label>
                    <Input
                      value={listingForm.contact_email || ''}
                      onChange={(e) => setListingForm({ ...listingForm, contact_email: e.target.value })}
                      placeholder="email@example.com"
                      className="input-underline"
                    />
                  </div>
                  <div>
                    <label className="text-[#A1A1AA] text-sm mb-2 block">Website</label>
                    <Input
                      value={listingForm.website_url || ''}
                      onChange={(e) => setListingForm({ ...listingForm, website_url: e.target.value })}
                      placeholder="https://..."
                      className="input-underline"
                    />
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button onClick={() => setShowCreateModal(false)} className="btn-outline-gold flex-1 py-3">
                    Cancel
                  </Button>
                  <Button onClick={createListing} className="btn-gold flex-1 py-3">
                    {myListing ? 'Update Listing' : 'Create Listing'}
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        )}

        {/* Create Collaboration Modal */}
        {showCollabModal && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="card-luxury p-8 max-w-lg w-full"
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-serif text-2xl text-white">Post Collaboration</h3>
                <button onClick={() => setShowCollabModal(false)} className="text-[#52525B] hover:text-white">
                  <X size={20} />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="text-[#A1A1AA] text-sm mb-2 block">Title *</label>
                  <Input
                    value={collabForm.title}
                    onChange={(e) => setCollabForm({ ...collabForm, title: e.target.value })}
                    placeholder="What are you looking for?"
                    className="input-underline"
                  />
                </div>

                <div>
                  <label className="text-[#A1A1AA] text-sm mb-2 block">Collaboration Type *</label>
                  <div className="grid grid-cols-2 gap-2">
                    {collaborationTypes.map(type => (
                      <button
                        key={type.id}
                        onClick={() => setCollabForm({ ...collabForm, collaboration_type: type.id })}
                        className={`p-3 text-left transition-all ${
                          collabForm.collaboration_type === type.id
                            ? 'bg-[#D4AF37]/20 border border-[#D4AF37] text-[#E5C585]'
                            : 'bg-[#0A0A0A] border border-[#27272A] text-[#A1A1AA]'
                        }`}
                      >
                        <span className="text-sm">{type.name}</span>
                        <p className="text-[10px] text-[#52525B] mt-1">{type.description}</p>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-[#A1A1AA] text-sm mb-2 block">Description *</label>
                  <Textarea
                    value={collabForm.description}
                    onChange={(e) => setCollabForm({ ...collabForm, description: e.target.value })}
                    placeholder="Describe the collaboration opportunity..."
                    className="bg-transparent border border-[#27272A] focus:border-[#D4AF37] rounded-none p-3 text-white min-h-[100px]"
                  />
                </div>

                <div>
                  <label className="text-[#A1A1AA] text-sm mb-2 block">Requirements (Optional)</label>
                  <Textarea
                    value={collabForm.requirements}
                    onChange={(e) => setCollabForm({ ...collabForm, requirements: e.target.value })}
                    placeholder="What you're looking for in a collaborator..."
                    className="bg-transparent border border-[#27272A] focus:border-[#D4AF37] rounded-none p-3 text-white min-h-[60px]"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button onClick={() => setShowCollabModal(false)} className="btn-outline-gold flex-1 py-3">
                    Cancel
                  </Button>
                  <Button onClick={createCollabPost} className="btn-gold flex-1 py-3">
                    Post Collaboration
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default BusinessDirectory;
